/**
 * Engage Relationships
 *
 * Agent-customer relationship records and history.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '@personic/shared'

interface Relationship {
  id: string
  profile_id: string
  agent_id: string
  interaction_count: number
  total_duration_seconds: number
  relationship_quality?: number
  rapport_score?: number
  effective_strategies: string[]
  avoided_topics: string[]
  successful_outcomes: number
  first_interaction?: string
  last_interaction?: string
}

export default function EngageRelationships() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRelationship, setSelectedRelationship] = useState<Relationship | null>(null)

  // For now, fetch profiles and then their relationships
  const { data: profiles, isLoading: profilesLoading } = useQuery({
    queryKey: ['profiles-for-relationships'],
    queryFn: async () => {
      const response = await api.get('/v3/profiles?limit=50')
      return response.data.profiles || []
    },
  })

  const { data: relationships, isLoading: relationshipsLoading } = useQuery({
    queryKey: ['engage-relationships', profiles],
    queryFn: async () => {
      if (!profiles || profiles.length === 0) return []

      const allRelationships: Relationship[] = []

      for (const profile of profiles.slice(0, 20)) {
        try {
          const response = await api.get(`/api/engage/v1/profile/${profile.id}/relationships`)
          const rels = response.data.relationships || []
          allRelationships.push(...rels.map((r: Relationship) => ({ ...r, profile })))
        } catch {
          // Skip profiles without relationships
        }
      }

      return allRelationships
    },
    enabled: !!profiles && profiles.length > 0,
  })

  const isLoading = profilesLoading || relationshipsLoading

  const filteredRelationships = relationships?.filter((rel: Relationship & { profile?: { name?: string } }) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      rel.agent_id.toLowerCase().includes(query) ||
      rel.profile?.name?.toLowerCase().includes(query)
    )
  })

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const getQualityColor = (quality?: number) => {
    if (!quality) return 'bg-gray-100 text-gray-600'
    if (quality >= 0.7) return 'bg-green-100 text-green-700'
    if (quality >= 0.4) return 'bg-yellow-100 text-yellow-700'
    return 'bg-red-100 text-red-700'
  }

  const getQualityLabel = (quality?: number) => {
    if (!quality) return 'Unknown'
    if (quality >= 0.7) return 'Strong'
    if (quality >= 0.4) return 'Developing'
    return 'Needs Attention'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relationships</h1>
          <p className="text-gray-600 mt-1">
            Track agent-customer relationship quality and history
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {filteredRelationships?.length || 0} relationships
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search by agent or profile..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
        />
      </div>

      {/* Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Relationships List */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading relationships...</div>
            </div>
          ) : filteredRelationships?.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="text-6xl mb-4">🤝</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Relationships Yet</h3>
              <p className="text-gray-500">
                Relationships are created automatically when agents interact with customers.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {filteredRelationships?.map((rel: Relationship & { profile?: { name?: string; id: string } }, index: number) => (
                  <motion.button
                    key={rel.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => setSelectedRelationship(rel)}
                    className={`w-full card p-4 text-left hover:shadow-lg transition-shadow ${
                      selectedRelationship?.id === rel.id ? 'ring-2 ring-engage-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-engage-primary/10 flex items-center justify-center">
                          <span className="text-engage-primary font-semibold text-lg">
                            {rel.agent_id.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{rel.agent_id}</p>
                          <p className="text-sm text-gray-500">
                            {rel.profile?.name || 'Unknown Customer'}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getQualityColor(rel.relationship_quality)}`}>
                        {getQualityLabel(rel.relationship_quality)}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-lg font-semibold text-gray-900">{rel.interaction_count}</p>
                        <p className="text-xs text-gray-500">Interactions</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">{formatDuration(rel.total_duration_seconds)}</p>
                        <p className="text-xs text-gray-500">Total Time</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">{rel.successful_outcomes}</p>
                        <p className="text-xs text-gray-500">Successful</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-gray-900">
                          {rel.rapport_score ? `${Math.round(rel.rapport_score * 100)}%` : '-'}
                        </p>
                        <p className="text-xs text-gray-500">Rapport</p>
                      </div>
                    </div>

                    {rel.last_interaction && (
                      <p className="mt-3 text-xs text-gray-400">
                        Last interaction: {new Date(rel.last_interaction).toLocaleDateString()}
                      </p>
                    )}
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          <div className="card sticky top-6">
            {selectedRelationship ? (
              <div className="divide-y divide-gray-100">
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Relationship Details</h3>
                  <p className="text-sm text-gray-500">
                    {selectedRelationship.agent_id} ↔ Customer
                  </p>
                </div>

                {/* Effective Strategies */}
                {selectedRelationship.effective_strategies.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Effective Strategies
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRelationship.effective_strategies.map((strategy, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full"
                        >
                          {strategy}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Avoided Topics */}
                {selectedRelationship.avoided_topics.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Topics to Avoid
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRelationship.avoided_topics.map((topic, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Timeline
                  </h4>
                  <div className="space-y-2 text-sm">
                    {selectedRelationship.first_interaction && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">First Contact</span>
                        <span className="text-gray-900">
                          {new Date(selectedRelationship.first_interaction).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {selectedRelationship.last_interaction && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Contact</span>
                        <span className="text-gray-900">
                          {new Date(selectedRelationship.last_interaction).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4 bg-gray-50">
                  <button className="w-full px-4 py-2 text-sm font-medium text-engage-primary hover:bg-engage-primary/10 rounded-lg transition-colors">
                    View Full History
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-4xl mb-3 opacity-50">👈</div>
                <p className="text-gray-500">
                  Select a relationship to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
